import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { PatientViewModel } from '../../patient/components/models/patient-view-model';
import { PrimaryAddress } from '../../shared/model/primary-address';
import { emailAddressValidator, phoneNumberValidator } from '../../shared/misc/regex';

/**
 * Check if IOC has all required fields completed.
 * Note: Risk class check is skipped as it's not required for enrollment via /pay
 */
export const checkIOCComplete = (encounter: PatientEncounter | null): boolean => {
  if (!encounter) return false;
  
  return (
    encounter.patientId > 0 &&
    encounter.facilityId !== -1 &&
    encounter.facilityId !== null &&
    encounter.clientId !== -1 &&
    encounter.clientId !== null &&
    encounter.payorRisk?.payorRiskId > 0 &&
    encounter.timingRisk?.timingRiskId > 0 &&
    encounter.pfrAmt > 0 &&
    (encounter.adjustedPfrAmt ?? 0) > 0 &&
    encounter.workflow?.workflowId > 0
  );
};

/**
 * Get list of missing IOC fields.
 */
export const getMissingIOCFields = (encounter: PatientEncounter | null): string[] => {
  if (!encounter) return ['IOC data not loaded'];
  
  const missing: string[] = [];
  
  if (!(encounter.facilityId !== -1 && encounter.facilityId !== null)) missing.push('Facility');
  if (!(encounter.clientId !== -1 && encounter.clientId !== null)) missing.push('Client');
  if (!(encounter.payorRisk?.payorRiskId > 0)) missing.push('Payor Risk');
  if (!(encounter.timingRisk?.timingRiskId > 0)) missing.push('Timing Risk');
  if (!(encounter.pfrAmt > 0)) missing.push('PFR Amount');
  if (!((encounter.adjustedPfrAmt ?? 0) > 0)) missing.push('Adjusted PFR Amount');
  if (!(encounter.workflow?.workflowId > 0)) missing.push('Workflow');
  
  return missing;
};

/**
 * Check if patient demographics are complete.
 */
export const checkDemographicsComplete = (patient: PatientViewModel | null): boolean => {
  if (!patient?.contact) return false;
  
  const { firstName, lastName, email, primPhoneNum } = patient.contact;
  
  return (
    firstName !== '' &&
    firstName !== null &&
    lastName !== '' &&
    lastName !== null &&
    email !== '' &&
    email !== null &&
    emailAddressValidator.test(email) &&
    primPhoneNum !== '' &&
    primPhoneNum !== null &&
    phoneNumberValidator.test(primPhoneNum)
  );
};

/**
 * Get list of missing demographic fields.
 */
export const getMissingDemographicFields = (patient: PatientViewModel | null): string[] => {
  if (!patient?.contact) return ['Patient data not loaded'];
  
  const missing: string[] = [];
  const { firstName, lastName, email, primPhoneNum } = patient.contact;
  
  if (!firstName || firstName === '') missing.push('First Name');
  if (!lastName || lastName === '') missing.push('Last Name');
  if (!email || email === '') {
    missing.push('Email');
  } else if (!emailAddressValidator.test(email)) {
    missing.push('Valid Email');
  }
  if (!primPhoneNum || primPhoneNum === '') {
    missing.push('Phone Number');
  } else if (!phoneNumberValidator.test(primPhoneNum)) {
    missing.push('Valid Phone Number');
  }
  
  return missing;
};

/**
 * Check if the account holder's address is complete.
 * Account holder is either the guarantor (if exists) or the patient.
 */
export const checkAccountHolderAddress = (
  encounter: PatientEncounter | null,
  patient: PatientViewModel | null
): boolean => {
  if (!encounter || !patient) return false;

  const thirdPartyGuarantor = encounter.patientChampion?.filter(
    (champion) => champion.isGuarantor
  ) || [];
  
  const accountHolderAddress: PrimaryAddress = thirdPartyGuarantor.length > 0 
    ? thirdPartyGuarantor[0]?.contact?.primaryAddress 
    : patient.contact?.primaryAddress;

  return !!(
    accountHolderAddress?.streetAddress1 && 
    accountHolderAddress?.city && 
    accountHolderAddress?.zipCode && 
    accountHolderAddress?.state?.stateCode
  );
};

/**
 * Check if PFR matches between IOC and payment program.
 */
export const checkPFRMatch = (encounter: PatientEncounter | null): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  return program.patientPaymentSchedule?.pfrAmt === encounter.pfrAmt;
};

/**
 * Check if the program is NOT a skeleton program.
 * A skeleton program is detected when:
 * - It's not a full-pay plan (paymentFreq !== 'F')
 * - AND both down payment is 0 AND (recurring amount < 1 OR terms < 1)
 * 
 * For non-full-pay programs, we require either:
 * - Recurring payment amount >= 1, OR
 * - Terms >= 1
 * 
 * @returns true if the program is valid (not a skeleton), false if skeleton detected
 */
export const checkNotSkeletonProgram = (encounter: PatientEncounter | null): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  const schedule = program.patientPaymentSchedule;
  const isFullPay = schedule?.paymentFreq === 'F';
  
  // Full pay plans are always valid
  if (isFullPay) return true;
  
  const downPaymentAmt = program.downPmtAmt || 0;
  const recurringAmt = schedule?.paymentDueAmt || 0;
  const terms = schedule?.terms || 0;
  
  // If both down payment and recurring are zero/minimal, it's a skeleton program
  const isZeroDown = downPaymentAmt === 0;
  const hasValidRecurring = recurringAmt >= 1 || terms >= 1;
  
  // Skeleton program: zero down AND no valid recurring setup
  if (isZeroDown && !hasValidRecurring) {
    return false;
  }
  
  return true;
};

/**
 * Check if down payment will be satisfied after current payment.
 */
export const checkDownPaymentWillBeSatisfied = (
  encounter: PatientEncounter | null,
  currentPaymentAmount: number = 0,
  pendingRemainingBalance?: number
): boolean => {
  if (!encounter) return false;
  
  const program = encounter.patientPaymentProgram?.[
    encounter.patientPaymentProgram.length - 1
  ];
  
  if (!program) return false;
  
  const downPaymentRequired = program.downPmtAmt || 0;
  
  // If zero-down plan, always satisfied
  if (downPaymentRequired === 0) return true;
  
  const pfrAmt = encounter.pfrAmt || 0;
  
  // Calculate previous payments made toward down payment
  // Previous payments = PFR - Pending Remaining Balance
  const remainingBalance = pendingRemainingBalance ?? pfrAmt;
  const previousPayments = pfrAmt - remainingBalance;
  
  // Total payments after this transaction
  const totalPaymentsAfterCurrent = previousPayments + currentPaymentAmount;
  
  // Check if total payments will satisfy the down payment requirement
  return totalPaymentsAfterCurrent >= downPaymentRequired;
};
